import { useQuery } from "@tanstack/react-query"

import useSlugExtractor from "@hooks/useSlugExtractor"

import replaceSlugs from "@utils/replaceSlugs"

import { CATALOG_ENDPOINTS } from "@endpoints/catalogs"

import { CatalogItemDetails, CatalogItemModifiersResponse } from "./ItemDetails.types"

export function useCatalogItemQuery(itemId: string) {
    const [itemEndpointTemplate] = useSlugExtractor([CATALOG_ENDPOINTS.ITEM_DETAILS])
    const itemEndpoint = replaceSlugs(itemEndpointTemplate, { id: itemId })

    return useQuery({
        queryKey: ["catalog-item", itemEndpoint],
        queryFn: async () => {
            const response = await fetch(itemEndpoint)

            if (!response.ok) {
                throw new Error(`Request to ${itemEndpoint} failed with status ${response.status}.`)
            }

            return (await response.json()) as CatalogItemDetails
        },
    })
}

export function useCatalogItemModifiersQuery(itemId: string) {
    const [modifiersEndpointTemplate] = useSlugExtractor([CATALOG_ENDPOINTS.ITEM_MODIFIERS])
    const modifiersEndpoint = replaceSlugs(modifiersEndpointTemplate, { id: itemId })

    // Modifiers are fetched just-in-time from the connected platform, so this runs as its own query that
    // resolves after the item detail render. Any failure means the platform is unreachable; the caller hides
    // the Options section rather than surfacing an error, which is why there is no bespoke retry override.
    return useQuery({
        queryKey: ["catalog-item-modifiers", modifiersEndpoint],
        queryFn: async () => {
            const response = await fetch(modifiersEndpoint)

            if (!response.ok) {
                throw new Error(`Request to ${modifiersEndpoint} failed with status ${response.status}.`)
            }

            return (await response.json()) as CatalogItemModifiersResponse
        },
    })
}
